import { BreadcrumbsAndTitle } from './BreadcrumbsAndTitle';
import { PolicySection } from './TermsAndConditionsPage';

const CookiesPolicyPage = () => {
  const lastUpdatedTs = 'November 1, 2024';
  return (
    <>
      <div className="mb-[6rem] w-full max-w-default px-[1.6rem] sm:px-default-padding-x">
        <BreadcrumbsAndTitle
          items={[
            { label: 'Home', href: '/' },
            {
              label: 'Cookies & Similar Technologies Policy',
              href: '#',
              current: true,
              currentCss: 'text-brand-black',
            },
          ]}
          title="GearFocus Cookies & Similar Technologies Policy"
        />
        <p className="text-[12px] italic">Effective Date {lastUpdatedTs}</p>
        <PolicySection title="">
          <p>
            At GearFocus, we use cookies and similar technologies to enhance your experience, 
            improve our Services, and ensure the security of your transactions. This Policy 
            explains how these technologies work, why we use them, and how you can manage 
            your preferences.
          </p>
        </PolicySection>
        <PolicySection title="1. Types of Cookie Technologies">
          <p>
            We use various types of cookies and related technologies to support functionality, personalization, and analytics:
          </p>
          <p className="mt-[2rem] font-semibold">Cookies</p>
          <ul className="list-disc pl-[3rem]">
            <li><b className="font-semibold">Session Cookies:</b> Temporary cookies that expire when you close your browser. These help navigate between pages without re-entering information.</li>
            <li><b className="font-semibold">Persistent Cookies:</b> Stored on your device for a set period, allowing us to remember preferences and recognize returning visitors.</li>
            <li><b className="font-semibold">Third-Party Cookies:</b> Placed by partners like analytics or ad providers to track browsing across different sites.</li>
          </ul>

          <p className="mt-[2rem] font-semibold">Other Technologies</p>
          <ul className="list-disc pl-[3rem]">
            <li><b className="font-semibold">Web Beacons::</b> Invisible graphics used to track email engagement or visits to specific web pages.</li>
            <li><b className="font-semibold">Social Widgets:</b> Buttons from social networks (like Facebook or Instagram) that allow sharing and interactions.</li>
            <li><b className="font-semibold">Local Storage Objects:</b> Data stored in your browser to remember site preferences and app settings.</li>
            <li><b className="font-semibold">SDKs (Software Development Kits):</b> Integrated third-party services within our mobile app that collect performance and interaction data.</li>
          </ul>
        </PolicySection>
        <PolicySection title="2. Why We Use These Technologies">
          <p>We use cookies and related technologies to:</p>
          <p className="mt-[2rem] font-semibold">Strictly Necessary (Security & Authentication)</p>
          <ul className="list-disc pl-[3rem]">
            <li>Ensure site security and detect fraud.</li>
            <li>Enable secure login and payment processing.</li>
          </ul>
          <p className="mt-[2rem] font-semibold">Site Performance & Analytics</p>
          <ul className="list-disc pl-[3rem]">
            <li>Track site performance and identify bugs.</li>
            <li>Understand how users engage with our Services to improve user experience.</li>
          </ul>
          <p className="mt-[2rem] font-semibold">Account & User Preferences</p>
          <ul className="list-disc pl-[3rem]">
            <li>Save your language, region, and display settings.</li>
            <li>Keep you logged in for future visits.</li>
          </ul>
          <p className="mt-[2rem] font-semibold">Marketing & Advertising</p>
          <ul className="list-disc pl-[3rem]">
            <li>Deliver personalized ads based on browsing behavior.</li>
            <li>Track ad effectiveness and manage promotional campaigns.</li>
          </ul>
        </PolicySection>
        <PolicySection title="3. Third-Party Services">
          <p>GearFocus partners with trusted third-party service providers such as:</p>
          <ul className="list-disc pl-[3rem]">
            <li><b className="font-semibold">Google Analytics:</b> To analyze site traffic and user engagement. Learn how to opt out here.</li>
            <li><b className="font-semibold">Facebook SDK:</b> For analytics and targeted ads. Manage preferences through <a href="https://www.facebook.com/settings?tab=ads" target="_blank" className="text-brand-secondary underline">Facebook Ad Settings</a>.</li>
          </ul>
          <p className="italic">These third parties may use cookies or similar technologies governed by their privacy policies.</p>
        </PolicySection>
        <PolicySection title="4. Managing Your Cookie Preferences">
          <p className="font-semibold">Browser Settings</p>
          <p>You can adjust your browser settings to control cookies:</p>
          <ul className="list-disc pl-[3rem]">
            <li><b className="font-semibold">Chrome:</b> <a href="https://support.google.com/chrome/answer/95647" target="_blank" className="text-brand-secondary underline">Manage cookies</a></li>
            <li><b className="font-semibold">Safari:</b> <a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" className="text-brand-secondary underline">Manage cookies</a></li>
            <li><b className="font-semibold">Firefox:</b> <a href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox" target="_blank" className="text-brand-secondary underline">Manage cookies</a></li>
            <li><b className="font-semibold">Microsoft Edge:</b> <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" className="text-brand-secondary underline">Manage cookies</a></li>
          </ul>

          <p className="mt-[2rem] font-semibold">Opt-Out Tools</p>
          <ul className="list-disc pl-[3rem]">
            <li><b className="font-semibold">Google Ads Settings:</b> Manage ad personalization <a href="https://adssettings.google.com/" target="_blank" className="text-brand-secondary underline">here</a>.</li>
            <li><b className="font-semibold">Network Advertising Initiative:</b> Learn about industry-wide opt-outs <a href="https://optout.networkadvertising.org/" target="_blank" className="text-brand-secondary underline">here</a>.</li>
          </ul>
        </PolicySection>
        <PolicySection title="5. Consent and Legitimate Interests">
          <p>By using GearFocus, you consent to our use of cookies and similar technologies as described in this Policy. You can withdraw consent at any time by adjusting your browser settings or disabling specific cookie types.</p>
        </PolicySection>
        <PolicySection title="6. Contact Us">
          <p>For questions about this Policy or managing cookie preferences, contact us at:</p>
          <p>Email: <a href="mailto:support@gearfocus.com" className="text-brand-secondary underline">support@gearfocus.com</a></p>
          <p>Mailing Address: GearFocus, 10903 Cade Cir, Austin, TX 78726</p>
          <p className="text-semibold">By using GearFocus, you agree to this Cookies & Similar Technologies Policy.</p>
        </PolicySection>
      </div>      
    </>
  );
};

export default CookiesPolicyPage;
